/**
 * branchApi.js
 *
 * When an org admin is managing a branch, the backend needs to know WHICH
 * facility to operate on. Most facility admin endpoints are scoped to the
 * logged-in user's facility via their auth token. For org admins we pass
 * ?facility=<id> query params or include facility in the body.
 *
 * This module mirrors the facilityAdmin API but adds the facility param.
 */
import axios from "axios";

const BASE_URL = process.env.REACT_APP_API_URL || "https://demo.queue.falits.com/api";

const api = axios.create({ baseURL: BASE_URL });

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("eq_access_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (err.response?.status === 401 && !original._retry) {
      original._retry = true;
      try {
        const refresh = localStorage.getItem("eq_refresh_token");
        const { data } = await axios.post(`${BASE_URL}/auth/token/refresh/`, { refresh });
        localStorage.setItem("eq_access_token", data.access);
        original.headers.Authorization = `Bearer ${data.access}`;
        return api(original);
      } catch {
        localStorage.removeItem("eq_access_token");
        localStorage.removeItem("eq_refresh_token");
        localStorage.removeItem("eq_user");
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

/**
 * Returns a facility-admin-equivalent API object scoped to a specific branchId.
 * Mirrors the facilityAdmin shape in api/index.js exactly.
 */
export function makeBranchApi(facilityId) {
  const p = { facility: facilityId }; // shorthand query param

  return {
    // Settings
    getSettings:    () => api.get("/facility/settings/", { params: p }),
    updateSettings: (data) => api.patch("/facility/settings/", { ...data, facility: facilityId }),

    // Kiosk fields
    listKioskFields:    () => api.get("/facility/kiosk-fields/", { params: p }),
    updateKioskField:   (id, data) => api.patch(`/facility/kiosk-fields/${id}/`, data),
    reorderKioskFields: (items) => api.post("/facility/kiosk-fields/reorder/", { items, facility: facilityId }),

    // Zones
    listZones:   () => api.get("/zones/", { params: p }),
    createZone:  (data) => api.post("/zones/", { ...data, facility: facilityId }),
    updateZone:  (id, data) => api.patch(`/zones/${id}/`, data),
    deleteZone:  (id) => api.delete(`/zones/${id}/`),

    // Stations
    listStations:  (params) => api.get("/stations/", { params: { ...params, ...p } }),
    createStation: (data) => api.post("/stations/", { ...data, facility: facilityId }),
    updateStation: (id, d) => api.patch(`/stations/${id}/`, d),
    deleteStation: (id) => api.delete(`/stations/${id}/`),

    // Counters
    listCounters:  (params) => api.get("/counters/", { params: { ...params, ...p } }),
    createCounter: (data) => api.post("/counters/", { ...data, facility: facilityId }),
    updateCounter: (id, data) => api.patch(`/counters/${id}/`, data),
    deleteCounter: (id) => api.delete(`/counters/${id}/`),

    // Outcomes
    listOutcomes:  (params) => api.get("/outcomes/", { params: { ...params, ...p } }),
    createOutcome: (data) => api.post("/outcomes/", { ...data, facility: facilityId }),
    updateOutcome: (id, data) => api.patch(`/outcomes/${id}/`, data),
    deleteOutcome: (id) => api.delete(`/outcomes/${id}/`),

    // Staff — branch staff only (role: staff or facility_admin)
    listStaff:          () => api.get("/auth/staff/", { params: p }),
    createStaff:        (data) => api.post("/auth/staff/", { ...data, facility: facilityId }),
    updateStaff:        (id, data) => api.patch(`/auth/staff/${id}/`, data),
    resetStaffPassword: (id, data) => api.post(`/auth/staff/${id}/reset-password/`, data),
    deactivateStaff:    (id) => api.post(`/auth/staff/${id}/deactivate/`),
    reactivateStaff:    (id) => api.post(`/auth/staff/${id}/reactivate/`),

    // Audio
    listAudio:   () => api.get("/audio/", { params: p }),
    uploadAudio: (data) => api.post("/audio/", data, { headers: { "Content-Type": "multipart/form-data" } }),
    deleteAudio: (id) => api.delete(`/audio/${id}/`),

    // Reports
    dailyReport:   (params) => api.get("/reports/daily/",    { params: { ...params, ...p } }),
    staffReport:   (params) => api.get("/reports/staff/",    { params: { ...params, ...p } }),
    stationReport: (params) => api.get("/reports/stations/", { params: { ...params, ...p } }),
    activityLog:   (params) => api.get("/activity-log/",     { params: { ...params, ...p } }),
    markLogsRead:  () => api.post("/activity-log/mark-read/"),
    unreadCount:   () => api.get("/activity-log/unread-count/"),
  };
}
